import { useEffect, useRef, useState } from "react";
import { Modal } from "antd";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "../CustomAntdComponents/Button";
import { Paths } from "../../../router/paths";
import {
  trackAnnualDiscountPromoModalClicked,
  trackAnnualDiscountPromoModalDismissed,
  trackAnnualDiscountPromoModalShown,
} from "../../../analytics/amplitude";
import { getAnnualDiscountModalRemainingDelayMs } from "../../../config/annualDiscountPromo";
import styles from "./AnnualDiscountModal.module.css";

/** Short pause so the popup never lands mid page load, even after the 30s have passed. */
const MIN_OPEN_DELAY_MS = 1000;

interface AnnualDiscountModalProps {
  open: boolean;
  percent: number;
  onDismiss: () => void;
}

const Sparkle = ({ className }: { className: string }) => (
  <svg
    className={className}
    width="18"
    height="18"
    viewBox="0 0 18 18"
    fill="none"
    aria-hidden
  >
    <path
      d="M9 1.2l1.35 5.1L15.8 9l-5.45 2.7L9 16.8l-1.35-5.1L2.2 9l5.45-2.7L9 1.2z"
      fill="currentColor"
    />
  </svg>
);

export const AnnualDiscountModal = ({
  open,
  percent,
  onDismiss,
}: AnnualDiscountModalProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [delayedOpen, setDelayedOpen] = useState(false);
  const tracked = useRef(false);

  useEffect(() => {
    if (!open) {
      setDelayedOpen(false);
      tracked.current = false;
      return;
    }

    const delay = Math.max(MIN_OPEN_DELAY_MS, getAnnualDiscountModalRemainingDelayMs());
    const timer = window.setTimeout(() => setDelayedOpen(true), delay);
    return () => window.clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    if (!delayedOpen || tracked.current) return;
    tracked.current = true;
    trackAnnualDiscountPromoModalShown();
  }, [delayedOpen]);

  const handleDismiss = () => {
    trackAnnualDiscountPromoModalDismissed();
    onDismiss();
  };

  const handleCta = () => {
    trackAnnualDiscountPromoModalClicked();
    onDismiss();
    navigate(Paths.SUBSCRIPTIONS);
  };

  return (
    <Modal
      open={delayedOpen}
      onCancel={handleDismiss}
      footer={null}
      width={420}
      centered
      closable={false}
      maskClosable={false}
      className={styles.modal}
      styles={{
        body: { padding: 0 },
        content: { padding: 0, overflow: "visible", borderRadius: 20 },
      }}
    >
      <div className={styles.card}>
        <div className={styles.hero}>
          <span className={styles.orbA} aria-hidden />
          <span className={styles.orbB} aria-hidden />
          <Sparkle className={`${styles.sparkle} ${styles.sparkleTl}`} />
          <Sparkle className={`${styles.sparkle} ${styles.sparkleBl}`} />
          <button
            type="button"
            className={styles.close}
            onClick={handleDismiss}
            aria-label={t("pages.annualDiscountPromo.close")}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden>
              <path
                d="M2 2l10 10M12 2L2 12"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
              />
            </svg>
          </button>
          <span className={styles.eyebrow}>
            {t("pages.annualDiscountPromo.modalEyebrow")}
          </span>
          <div className={styles.percentBlock}>
            <span className={styles.percent}>
              {t("pages.annualDiscountPromo.percentMark", { percent })}
            </span>
            <span className={styles.off}>
              {t("pages.annualDiscountPromo.modalOff")}
            </span>
          </div>
          <p className={styles.heroLabel}>
            {t("pages.annualDiscountPromo.modalHeroLabel")}
          </p>
        </div>

        <div className={styles.perforation} aria-hidden>
          <span className={styles.notchLeft} />
          <span className={styles.dash} />
          <span className={styles.notchRight} />
        </div>

        <div className={styles.body}>
          <h2 className={styles.headline}>
            {t("pages.annualDiscountPromo.modalHeadline", { percent })}
          </h2>
          <p className={styles.copy}>
            {t("pages.annualDiscountPromo.modalBody", { percent })}
          </p>

          <div className={styles.compare} aria-hidden>
            <div className={`${styles.compareCol} ${styles.compareMuted}`}>
              <span className={styles.compareLabel}>
                {t("pages.annualDiscountPromo.compareMonthly")}
              </span>
              <span className={styles.compareValue}>
                {t("pages.annualDiscountPromo.compareFullPrice")}
              </span>
            </div>
            <span className={styles.compareVs}>
              {t("pages.annualDiscountPromo.compareVs")}
            </span>
            <div className={`${styles.compareCol} ${styles.compareHighlight}`}>
              <span className={styles.compareLabel}>
                {t("pages.annualDiscountPromo.compareAnnual")}
              </span>
              <span className={styles.compareValue}>
                {t("pages.annualDiscountPromo.compareHalfOff", { percent })}
              </span>
            </div>
          </div>

          <Button
            type="primary"
            block
            size="large"
            onClick={handleCta}
            className={styles.primary}
          >
            {t("pages.annualDiscountPromo.cta")}
          </Button>
          <button
            type="button"
            className={styles.later}
            onClick={handleDismiss}
          >
            {t("pages.annualDiscountPromo.maybeLater")}
          </button>
        </div>
      </div>
    </Modal>
  );
};
